"use client";

import { Search } from "lucide-react";
import { motion } from "framer-motion";
import Link from "next/link";
import { openCommandPalette } from "@/components/common/command-palette";
import { EASE_OUT_EXPO, WORD_STAGGER, WORD_CHILD } from "@/lib/animations";
import { wikiLink } from "@/lib/url";

const POPULAR = ["보톡스", "필러", "레이저토닝", "울쎄라", "슈링크", "리쥬란"];

export function HeroSection() {
  return (
    <section className="relative -ml-[calc(50vw-50%)] w-[100vw] overflow-hidden">
      <div className="hero-mesh-bg absolute inset-0 -z-10" />

      <div className="mx-auto flex max-w-xl flex-col items-center px-6 pt-20 pb-14 text-center lg:pt-28 lg:pb-20">
        {/* Badge — 골드 테두리 럭셔리 */}
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.5, ease: EASE_OUT_EXPO }}
          className="mb-6 inline-flex items-center gap-1.5 rounded-full border border-amber-200/60 bg-gradient-to-r from-amber-50/80 to-violet-50/80 px-5 py-2 text-xs font-bold tracking-[1.5px] text-violet-700 uppercase backdrop-blur-sm"
        >
          임상 데이터 기반 시술 백과
        </motion.p>

        {/* Headline */}
        <motion.h1
          variants={WORD_STAGGER}
          initial="hidden"
          animate="visible"
          className="mb-5 text-[42px] leading-[1.1] font-extrabold tracking-[-2.5px] text-gray-900 lg:text-[56px]"
        >
          <motion.span
            variants={WORD_CHILD}
            className="inline-block bg-gradient-to-r from-violet-700 to-violet-500 bg-clip-text text-transparent"
          >
            시술&nbsp;정보
          </motion.span>
          <motion.span variants={WORD_CHILD} className="inline-block">
            ,
          </motion.span>
          <br />
          <motion.span variants={WORD_CHILD} className="inline-block">
            똑똑하게
          </motion.span>
          <br />
          <motion.span variants={WORD_CHILD} className="inline-block">
            찾자.
          </motion.span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.5, ease: EASE_OUT_EXPO }}
          className="mb-10 text-base leading-relaxed tracking-wide text-gray-400"
        >
          600+ 시술의 효과 · 부작용 · 임상근거를 한눈에
        </motion.p>

        {/* Search trigger — 럭셔리 그림자 */}
        <motion.button
          initial={{ opacity: 0, y: 15, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ delay: 0.5, duration: 0.5, ease: EASE_OUT_EXPO }}
          onClick={openCommandPalette}
          className="search-pulse relative mx-auto flex w-full max-w-md items-center gap-3 rounded-2xl border border-gray-200/60 bg-white py-4.5 pr-4 pl-5 text-left shadow-lg shadow-gray-200/40 transition-all hover:border-violet-300 hover:shadow-xl active:scale-[0.98]"
        >
          <Search className="h-5 w-5 shrink-0 text-violet-400" strokeWidth={1.8} />
          <span className="flex-1 text-base text-gray-400">보톡스, 주름, 리프팅 검색...</span>
          <kbd className="hidden items-center rounded-lg border border-gray-100 bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-400 sm:inline-flex">
            ⌘K
          </kbd>
        </motion.button>

        {/* Popular searches */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7, duration: 0.5 }}
          className="scrollbar-none mt-6 flex items-center gap-2 overflow-x-auto"
        >
          <span className="shrink-0 text-xs font-semibold tracking-wider text-gray-300 uppercase">
            인기
          </span>
          {POPULAR.map((term) => (
            <Link
              key={term}
              href={wikiLink(term)}
              className="shrink-0 rounded-full border border-gray-200/60 bg-white/70 px-3.5 py-1.5 text-xs font-medium text-gray-500 transition-all hover:border-violet-300 hover:bg-violet-50 hover:text-violet-600 active:scale-95"
            >
              {term}
            </Link>
          ))}
        </motion.div>

        {/* 신뢰 지표 */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9, duration: 0.5 }}
          className="text-muted-foreground mt-6 flex items-center justify-center gap-4 text-xs"
        >
          <span className="flex items-center gap-1">
            <span className="font-bold text-violet-600">4.5만편</span> 논문 분석
          </span>
          <span className="h-3 w-px bg-gray-300" />
          <span className="flex items-center gap-1">
            <span className="font-bold text-violet-600">1,173개</span> 시술 수록
          </span>
          <span className="h-3 w-px bg-gray-300" />
          <span className="flex items-center gap-1">
            <span className="font-bold text-violet-600">실시간</span> AI 분석
          </span>
        </motion.div>
      </div>
    </section>
  );
}
